import React from "react";
import { Form, Formik } from "formik";
import { motion } from "framer-motion";
import { FiSave, FiCalendar, FiBook, FiAward } from "react-icons/fi";
import { NCEValidation } from "../../utils/validations";
import { FormInput } from "../../components/ui/FormInput";
import { useQueryClient } from "@tanstack/react-query";
import { axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";

const NCE = ({ setdata, data, onClose }) => {
  const queryClient = useQueryClient();

  const _save = async (values) => {
    try {
      const req = await axiospost("ms_applicant_alevel", {
        type: "NCE",
        results: values,
      });
      if (req.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { data: result } = req;
      if (result && result.type === "success") {
        const { message, type } = result;
        swal(type, message);
        queryClient.invalidateQueries({ queryKey: ["aLevelResult"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
        // Close modal after successful save
        if (onClose) {
          setTimeout(onClose, 1500);
        }
      } else {
        throw new Error(result.message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  return (
    <Formik
      initialValues={{
        institutionName: data ? data?.institutionName : "",
        yearOfGraduation: data ? data?.yearOfGraduation : "",
        educationSubject: data ? data?.educationSubject : "",
        educationSubjectGrade: data ? data?.educationSubjectGrade : "",
        educationSubject2: data ? data?.educationSubject2 : "",
        educationSubject2Grade: data ? data?.educationSubject2Grade : "",
        educationSubject3: data ? data?.educationSubject3 : "",
        educationSubject3Grade: data ? data?.educationSubject3Grade : "",
      }}
      validationSchema={NCEValidation}
      initialTouched={{ zip: true }}
      onSubmit={_save}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-6">
          {/* Institution Details Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-neutral-700 pb-2 border-b border-neutral-200">
              <FiBook className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Institution Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-7">
                <FormInput
                  name="institutionName"
                  label="Institution Name"
                  type="text"
                  required
                  helperText="Enter the full name of your NCE institution"
                />
              </div>
              <div className="md:col-span-5">
                <FormInput
                  name="yearOfGraduation"
                  label="Year of Graduation"
                  type="text"
                  leftIcon={<FiCalendar />}
                  required
                  helperText="e.g., 2023"
                />
              </div>
            </div>
          </div>

          {/* Subject 1 Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-neutral-700 pb-2 border-b border-neutral-200">
              <FiAward className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Teaching Subject 1</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-7">
                <FormInput
                  name="educationSubject"
                  label="Subject Name"
                  type="text"
                  leftIcon={<FiBook />}
                  required
                  helperText="Enter your first teaching subject"
                />
              </div>
              <div className="md:col-span-5">
                <FormInput
                  name="educationSubjectGrade"
                  label="Grade"
                  type="text"
                  required
                  helperText="e.g., A, B, C, D, E"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Subject 2 Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-neutral-700 pb-2 border-b border-neutral-200">
              <FiAward className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Teaching Subject 2</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-7">
                <FormInput
                  name="educationSubject2"
                  label="Subject Name"
                  type="text"
                  leftIcon={<FiBook />}
                  required
                  helperText="Enter your second teaching subject"
                />
              </div>
              <div className="md:col-span-5">
                <FormInput
                  name="educationSubject2Grade"
                  label="Grade"
                  type="text"
                  required
                  helperText="e.g., A, B, C, D, E"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Subject 3 Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-neutral-700 pb-2 border-b border-neutral-200">
              <FiAward className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Teaching Subject 3</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-7">
                <FormInput
                  name="educationSubject3"
                  label="Subject Name"
                  type="text"
                  leftIcon={<FiBook />}
                  required
                  helperText="Enter your third teaching subject"
                />
              </div>
              <div className="md:col-span-5">
                <FormInput
                  name="educationSubject3Grade"
                  label="Grade"
                  type="text"
                  required
                  helperText="e.g., A, B, C, D, E"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="font-medium">Saving...</span>
                </>
              ) : (
                <>
                  <FiSave className="w-5 h-5" />
                  <span className="font-medium">Save NCE Results</span>
                </>
              )}
            </motion.button>
          </div>

          {/* Help Note */}
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-warning-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-warning-900 mb-1">
                  Important Note
                </h4>
                <p className="text-sm text-warning-700">
                  Ensure all grades are entered correctly. Use capital letters (A, B, C, D, E) for
                  grades. The information you provide must match your official NCE certificate. NCE
                  holders should enter their teaching subjects as studied.
                </p>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default NCE;
